import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Building2,
  CheckCircle2,
  CircleAlert,
  FileCheck2,
  RefreshCw,
  ShieldCheck,
  UserCheck,
} from 'lucide-react';
import {
  investorService,
  type InvestorAccountEnvelope,
  type InvestorKycProfile,
} from '@/services/investorService';
import { Loading, ErrorState, UnavailableState } from '@/components/eds';

// WP2.2 I-09: message-based 403/401 classification (service layer is frozen).
const isRestrictedError = (msg: string): boolean => /\b403\b|forbidden|permission|restricted/i.test(msg);

type Props = {
  account: InvestorAccountEnvelope;
};

const readableStatus = (value?: string | null) =>
  value
    ? value
        .replace(/[_-]/g, ' ')
        .replace(/\b\w/g, (letter) => letter.toUpperCase())
    : 'Not Started';

const valueOrFallback = (
  ...values: Array<string | null | undefined>
): string => {
  const value = values.find(
    (item) => typeof item === 'string' && item.trim().length > 0
  );

  return value?.trim() || 'Not provided';
};

const GovernanceItem: React.FC<{
  label: string;
  value: string;
  complete: boolean;
}> = ({ label, value, complete }) => (
  <div className="flex items-start justify-between gap-4 py-3 border-b border-eds-border last:border-b-0">
    <div>
      <div className="text-xs uppercase tracking-wider text-eds-muted-fg">
        {label}
      </div>
      <div className="mt-1 text-sm font-medium text-eds-fg">{value}</div>
    </div>

    <div
      className={`shrink-0 inline-flex items-center gap-1 rounded-eds-pill px-2 py-1 text-xs font-medium ${
        complete
          ? 'bg-eds-success/10 text-eds-success'
          : 'bg-eds-warning/10 text-eds-warning'
      }`}
    >
      {complete ? (
        <CheckCircle2 className="w-3.5 h-3.5" />
      ) : (
        <CircleAlert className="w-3.5 h-3.5" />
      )}
      {complete ? 'Complete' : 'Required'}
    </div>
  </div>
);

const OrganisationGovernanceSummary: React.FC<Props> = ({ account }) => {
  const [kyc, setKyc] = useState<InvestorKycProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadRestricted, setLoadRestricted] = useState(false);

  const loadKyc = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setLoadRestricted(false);

    try {
      const result = await investorService.getKycProfile();
      setKyc(result);
    } catch (error) {
      const raw = error instanceof Error ? error.message : '';
      const restricted = isRestrictedError(raw);
      setLoadRestricted(restricted);
      setLoadError(
        restricted
          ? 'You do not currently have permission to view organisation governance for this account.'
          : 'Governance information could not be loaded right now. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadKyc();
  }, [loadKyc]);

  const company = account.company;

  const organisationName = valueOrFallback(
    company?.legal_name,
    company?.name,
    company?.organization_name,
    company?.trading_name,
    kyc?.organizationName
  );

  const registrationNumber = valueOrFallback(
    company?.registration_number,
    kyc?.organizationRegistration
  );

  const registeredAddress = valueOrFallback(
    company?.headquarters_address,
    kyc?.organizationAddress
  );

  const businessCategory = valueOrFallback(
    company?.sector,
    company?.sub_sector,
    kyc?.businessCategory
  );

  const representativeName = valueOrFallback(kyc?.representativeName);
  const representativeRole = valueOrFallback(kyc?.representativeRole);
  const representativeEmail = valueOrFallback(kyc?.representativeEmail);

  const organisationChecks = useMemo(
    () => [
      Boolean(organisationName && organisationName !== 'Not provided'),
      Boolean(
        registrationNumber && registrationNumber !== 'Not provided'
      ),
      Boolean(registeredAddress && registeredAddress !== 'Not provided'),
      Boolean(businessCategory && businessCategory !== 'Not provided'),
      Boolean(company?.tax_id || kyc?.taxId),
      Boolean(
        company?.incorporation_country || kyc?.countryOfOperation
      ),
    ],
    [
      organisationName,
      registrationNumber,
      registeredAddress,
      businessCategory,
      company?.tax_id,
      company?.incorporation_country,
      kyc?.taxId,
      kyc?.countryOfOperation,
    ]
  );

  const organisationCompletion = Math.round(
    (organisationChecks.filter(Boolean).length /
      organisationChecks.length) *
      100
  );

  const representativeComplete =
    representativeName !== 'Not provided' &&
    representativeEmail !== 'Not provided';

  if (loading) {
    // WP2.2 I-08: frozen EDS governed Loading state.
    return <div className="border border-eds-border rounded-eds-lg p-5"><Loading label="Loading organisation governance status…" /></div>;
  }

  return (
    <section className="border border-eds-border rounded-eds-lg overflow-hidden bg-eds-surface">
      <div className="px-5 py-4 border-b border-eds-border bg-eds-surface-2 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-eds-success" />
            <h3 className="font-semibold text-eds-fg">
              Organisation Governance Summary
            </h3>
          </div>
          <p className="text-sm text-eds-muted-fg mt-1">
            Review organisation identity, KYC readiness and authorised
            representative information.
          </p>
        </div>

        <button
          type="button"
          onClick={() => void loadKyc()}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-eds-md border border-eds-border text-eds-fg hover:bg-eds-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh governance
        </button>
      </div>

      {loadError && (
        // WP2.2 I-08/I-09: governed EDS state; 403 → Restricted (in-place),
        // otherwise a friendly retryable error. Never a raw backend object.
        <div className="mx-5 mt-5">
          {loadRestricted ? (
            <UnavailableState phase="Permission required" title="Restricted" message={loadError} />
          ) : (
            <ErrorState title="Governance information unavailable" message={loadError} onRetry={() => void loadKyc()} />
          )}
        </div>
      )}

      <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-eds-md border border-eds-border p-4">
          <div className="flex items-center gap-2 text-eds-muted-fg">
            <Building2 className="w-4 h-4" />
            <span className="text-xs uppercase tracking-wider">
              Organisation profile
            </span>
          </div>
          <div className="mt-3 text-2xl font-semibold text-eds-fg">
            {organisationCompletion}%
          </div>
          <div className="mt-2 h-2 rounded-eds-pill bg-eds-surface-2 overflow-hidden">
            <div
              className="h-full bg-eds-success rounded-eds-pill transition-all"
              style={{ width: `${organisationCompletion}%` }}
            />
          </div>
          <p className="mt-2 text-xs text-eds-muted-fg">
            Based on company identity, registration, tax, address and
            jurisdiction information.
          </p>
        </div>

        <div className="rounded-eds-md border border-eds-border p-4">
          <div className="flex items-center gap-2 text-eds-muted-fg">
            <FileCheck2 className="w-4 h-4" />
            <span className="text-xs uppercase tracking-wider">
              KYC readiness
            </span>
          </div>
          <div className="mt-3 text-2xl font-semibold text-eds-fg">
            {Number(kyc?.completenessScore || 0)}%
          </div>
          <div className="mt-2 text-sm font-medium text-eds-fg">
            {readableStatus(kyc?.kycStatus)}
          </div>
          <p className="mt-2 text-xs text-eds-muted-fg">
            Controlled by the existing GIISDEC investor KYC workflow.
          </p>
        </div>

        <div className="rounded-eds-md border border-eds-border p-4">
          <div className="flex items-center gap-2 text-eds-muted-fg">
            <UserCheck className="w-4 h-4" />
            <span className="text-xs uppercase tracking-wider">
              Representative
            </span>
          </div>
          <div className="mt-3 text-lg font-semibold text-eds-fg">
            {representativeComplete ? 'Recorded' : 'Incomplete'}
          </div>
          <div className="mt-2 text-sm text-eds-fg">
            {representativeName}
          </div>
          <p className="mt-2 text-xs text-eds-muted-fg">
            Representative details remain governed through the KYC workflow.
          </p>
        </div>
      </div>

      <div className="px-5 pb-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="rounded-eds-md border border-eds-border px-4">
          <div className="py-4 border-b border-eds-border">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-eds-success" />
              <h4 className="font-semibold text-eds-fg">
                Legal Entity Controls
              </h4>
            </div>
          </div>

          <GovernanceItem
            label="Legal organisation name"
            value={organisationName}
            complete={organisationName !== 'Not provided'}
          />
          <GovernanceItem
            label="Registration number"
            value={registrationNumber}
            complete={registrationNumber !== 'Not provided'}
          />
          <GovernanceItem
            label="Registered address"
            value={registeredAddress}
            complete={registeredAddress !== 'Not provided'}
          />
          <GovernanceItem
            label="Business category"
            value={businessCategory}
            complete={businessCategory !== 'Not provided'}
          />
          <GovernanceItem
            label="Tax identification"
            value={valueOrFallback(company?.tax_id, kyc?.taxId)}
            complete={Boolean(company?.tax_id || kyc?.taxId)}
          />
          <GovernanceItem
            label="Jurisdiction / country of operation"
            value={valueOrFallback(
              company?.incorporation_country,
              kyc?.countryOfOperation
            )}
            complete={Boolean(
              company?.incorporation_country || kyc?.countryOfOperation
            )}
          />
        </div>

        <div className="rounded-eds-md border border-eds-border px-4">
          <div className="py-4 border-b border-eds-border">
            <div className="flex items-center gap-2">
              <UserCheck className="w-4 h-4 text-eds-success" />
              <h4 className="font-semibold text-eds-fg">
                Authorised Representative
              </h4>
            </div>
          </div>

          <GovernanceItem
            label="Representative name"
            value={representativeName}
            complete={representativeName !== 'Not provided'}
          />
          <GovernanceItem
            label="Role or title"
            value={representativeRole}
            complete={representativeRole !== 'Not provided'}
          />
          <GovernanceItem
            label="Representative email"
            value={representativeEmail}
            complete={representativeEmail !== 'Not provided'}
          />

          <div className="my-4 rounded-eds-md border border-eds-info/30 bg-eds-info/10 px-4 py-3">
            <div className="flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 mt-0.5 text-eds-info" />
              <div>
                <div className="text-sm font-medium text-eds-fg">
                  Governance control
                </div>
                <p className="mt-1 text-xs text-eds-fg">
                  Representative information is currently maintained through
                  the KYC Centre. Multiple representatives, delegated
                  authorities and revocation controls will be introduced after
                  the dedicated backend governance module is approved.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OrganisationGovernanceSummary;
